import { generateCaptchaTokenResult } from '@/lib/captcha';
import { useTranslation } from '@/lib/i18n';
import { trpc } from '@/lib/trpc';
import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { useAppForm } from '@/lib/use-app-form';
import { cn } from '@worldcoo/ui/lib/utils';
import { toast } from '@/lib/toast';
import { ArrowUpRight } from 'lucide-react';
import * as z from 'zod';
import { Form } from '../../-components/form';
import { FORM_ERROR_COLORS } from './primitives';

/**
 * Compact newsletter subscription used in the footer (Figma: label + email
 * input + dark circular arrow button). Same backend contract as the current
 * `www` newsletter — trpc `payments.postContactForm` with category `newsletter`.
 */
export function NewsletterForm({ className }: { className?: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const {
		mutateAsync: postContactForm,
		isPending,
		isSuccess,
	} = useMutation(trpc.payments.postContactForm.mutationOptions());

	const form = useAppForm({
		defaultValues: { email: '' },
		validators: {
			onSubmit: z.object({ email: z.email(t`Invalid email`) }),
		},
		onSubmit: async ({ value }) => {
			const { data: captchaToken } = await generateCaptchaTokenResult();
			if (!captchaToken) {
				toast.error(t`Captcha verification failed. Please try again.`);
				return;
			}
			const result = await postContactForm({
				email: value.email,
				category: 'newsletter',
				message: 'Newsletter subscription',
				name: '',
				phone: '',
				checkbox: { type: 'termsAndConditions', checked: true, text: '', label: '' },
				captchaToken,
				language,
			});
			if (!result.captchaPassed) {
				toast.error(t`Captcha verification failed. Please try again.`);
			} else {
				toast.success(t`You have successfully subscribed to the newsletter!`);
			}
		},
	});

	return (
		<div className={cn('flex flex-col gap-3', FORM_ERROR_COLORS, className)}>
			<p className="text-olive-foreground/90 text-sm font-medium">{t`Subscribe to our newsletter with impact`}</p>
			<Form handleSubmit={() => form.handleSubmit()} className="flex items-center gap-2">
				{/* The hand-off FILLS this field rather than outlining it. Sampling the mockup gives #9FA567
				    against the card's #B2B873 — the card colour times 0.895 on all three channels, i.e. plain
				    black at 10% rather than a separate olive token. Expressed that way, the field follows along
				    if the card's green is ever retuned.

				    Targeted with `[&_input]` because TextField's `className` lands on its OUTERMOST div and it
				    builds the input's own class list internally, with no prop to reach it. Styling the wrapper
				    instead just paints a darker band around a still-transparent, still-outlined input. The
				    descendant selector also outranks the Input's base classes, so no `!` is needed. */}
				<form.AppField
					name="email"
					children={(field) => (
						<field.TextField
							type="email"
							placeholder={t`example@email.com`}
							className="[&_input]:text-olive-foreground [&_input::placeholder]:text-olive-foreground/60 [&_input]:h-11 [&_input]:border-transparent [&_input]:bg-black/10"
						/>
					)}
				/>
				<button
					type="submit"
					disabled={isPending || isSuccess}
					aria-label={t`Subscribe`}
					className="bg-ink grid size-11 shrink-0 place-items-center rounded-full text-white transition-opacity hover:opacity-90 disabled:opacity-50"
				>
					<ArrowUpRight className="size-5" />
				</button>
			</Form>
		</div>
	);
}
